import { z } from "zod";

import type {
  AuthSession,
  AvatarUpload,
  ChangePasswordInput,
  CompleteInviteOnboardingInput,
  GoogleCredentialInput,
  GoogleRedirectCompleteInput,
  GoogleRedirectCompletion,
  GoogleSignupInput,
  LoginInput,
  LoginResult,
  MfaConfirmInput,
  MfaDisableInput,
  MfaEnrollmentInput,
  MfaLoginVerifyInput,
  MfaPolicy,
  MfaStatus,
  MfaTrustedDevice,
  ProfileAvatarInput,
  ProfileInput,
  ResetPasswordInput,
  SignupInput,
  User,
} from "@/lib/auth/auth-types";

import { apiGet, apiMutate, apiUpload } from "@/lib/api/client";
import {
  authResponseSchema,
  avatarUploadSchema,
  mfaEnrollmentSchema,
  mfaPolicySchema,
  mfaRecoveryCodesSchema,
  mfaStatusSchema,
  mfaTrustedDeviceSchema,
  userSchema,
} from "@/lib/auth/auth-types";

export {
  authResponseSchema,
  avatarUploadSchema,
  mfaEnrollmentSchema,
  mfaPolicySchema,
  mfaRecoveryCodesSchema,
  mfaStatusSchema,
  mfaTrustedDeviceSchema,
  userSchema,
} from "@/lib/auth/auth-types";
export type {
  AuthSession,
  AvatarUpload,
  ChangePasswordInput,
  CompanySwitchPrompt,
  CompleteInviteOnboardingInput,
  GoogleCredentialInput,
  GoogleRedirectCompleteInput,
  GoogleRedirectCompletion,
  GoogleSignupInput,
  InvitationOption,
  LoginInput,
  LoginResult,
  MfaChallenge,
  MfaConfirmInput,
  MfaDisableInput,
  MfaEnrollmentInput,
  MfaLoginVerifyInput,
  MfaPolicy,
  MfaStatus,
  MfaTrustedDevice,
  OnboardingChallenge,
  ProfileAvatarInput,
  ProfileInput,
  ResetPasswordInput,
  SignupInput,
  User,
} from "@/lib/auth/auth-types";

const messageResponseSchema = z.object({ message: z.string() });
const googleLinkStatusSchema = z.object({ connected: z.boolean() });
const googleRedirectCompletionSchema = z.object({
  status: z.enum(["authenticated", "organization_required"]),
  login: authResponseSchema.nullable().optional(),
});

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

function parseLoginResponse(data: unknown): LoginResult {
  const parsed = authResponseSchema.parse(data);
  if (parsed.onboardingRequired) {
    if (
      !parsed.onboardingChallenge ||
      !parsed.onboardingEmail ||
      parsed.accessToken ||
      parsed.mfaRequired ||
      parsed.user ||
      parsed.onboardingOptions.length === 0
    ) {
      throw new Error("Invalid invitation onboarding response");
    }
    return {
      kind: "onboarding",
      challenge: parsed.onboardingChallenge,
      email: parsed.onboardingEmail,
      options: parsed.onboardingOptions,
    };
  }
  if (parsed.mfaRequired) {
    if (!parsed.mfaChallenge || parsed.accessToken || !parsed.user)
      throw new Error("Invalid MFA challenge response");
    return {
      kind: "mfa",
      challenge: parsed.mfaChallenge,
      user: parsed.user,
      isTrustedDevice: parsed.mfaTrustedDevice,
    };
  }
  if (!parsed.accessToken || !parsed.user) throw new Error("Invalid login response");
  return {
    accessToken: parsed.accessToken,
    companySwitchOptions: parsed.companySwitchOptions,
    user: parsed.user,
  };
}

/** `POST /v1/auth/signup`. */
export async function signup(input: SignupInput): Promise<AuthSession> {
  const data = await apiMutate<unknown>("POST", "/auth/signup", {
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
  const parsed = authResponseSchema.parse(data);
  if (parsed.mfaRequired || !parsed.accessToken || !parsed.user)
    throw new Error("Invalid signup response");
  return {
    accessToken: parsed.accessToken,
    companySwitchOptions: parsed.companySwitchOptions,
    user: parsed.user,
  };
}

/** `POST /v1/auth/google/signup` — the credential is verified by the backend. */
export async function googleSignup(input: GoogleSignupInput): Promise<AuthSession> {
  const data = await apiMutate<unknown>("POST", "/auth/google/signup", {
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
  const parsed = authResponseSchema.parse(data);
  if (parsed.mfaRequired || !parsed.accessToken || !parsed.user)
    throw new Error("Invalid Google signup response");
  return {
    accessToken: parsed.accessToken,
    companySwitchOptions: parsed.companySwitchOptions,
    user: parsed.user,
  };
}

/** `POST /v1/auth/login`. Errors are a generic UNAUTHENTICATED code — callers must not branch UI copy on it. */
export async function login(input: LoginInput): Promise<LoginResult> {
  const data = await apiMutate<unknown>("POST", "/auth/login", {
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
  return parseLoginResponse(data);
}

/** `POST /v1/auth/google/login` — the credential is verified by the backend. */
export async function googleLogin(input: GoogleCredentialInput): Promise<LoginResult> {
  const data = await apiMutate<unknown>("POST", "/auth/google/login", {
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
  return parseLoginResponse(data);
}

/** Completes the same-tab Google redirect handoff. */
export async function completeGoogleRedirect(
  input: GoogleRedirectCompleteInput,
): Promise<GoogleRedirectCompletion> {
  const data = await apiMutate<unknown>("POST", "/auth/google/redirect/complete", {
    body: {
      code: input.code,
      ...(input.tenantName ? { tenantName: input.tenantName } : {}),
    },
    idempotencyKey: newIdempotencyKey(),
  });
  const parsed = googleRedirectCompletionSchema.parse(data);
  if (parsed.status === "organization_required") {
    return { status: parsed.status };
  }
  if (!parsed.login) throw new Error("Invalid Google redirect response");
  const result = parseLoginResponse(parsed.login);
  if ("kind" in result && result.kind === "onboarding") {
    throw new Error("Google sign-in cannot complete invitation onboarding");
  }
  return { status: parsed.status, result };
}

/** `POST /v1/auth/google/link` — requires the current platform session. */
export async function linkGoogle(input: GoogleCredentialInput): Promise<string> {
  const data = await apiMutate<unknown>("POST", "/auth/google/link", {
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
  return messageResponseSchema.parse(data).message;
}

/** `GET /v1/auth/google/link` — reports whether Google sign-in is linked to the current user. */
export async function getGoogleLinkStatus(
  signal?: AbortSignal,
): Promise<{ connected: boolean }> {
  const data = await apiGet<unknown>("/auth/google/link", signal);
  return googleLinkStatusSchema.parse(data);
}

/** `DELETE /v1/auth/google/link` — removes the current user's Google sign-in identity. */
export async function unlinkGoogle(): Promise<string> {
  const data = await apiMutate<unknown>("DELETE", "/auth/google/link", {
    idempotencyKey: newIdempotencyKey(),
  });
  return messageResponseSchema.parse(data).message;
}

/** Completes a short-lived MFA login challenge and returns a normal session. */
export async function verifyMfaLogin(input: MfaLoginVerifyInput): Promise<AuthSession> {
  const data = await apiMutate<unknown>("POST", "/auth/mfa/login/verify", {
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
  const parsed = authResponseSchema.parse(data);
  if (parsed.mfaRequired || !parsed.accessToken || !parsed.user)
    throw new Error("Invalid MFA verification response");
  return {
    accessToken: parsed.accessToken,
    companySwitchOptions: parsed.companySwitchOptions,
    user: parsed.user,
  };
}

/** Completes the mandatory first-login setup for an email invitation. */
export async function completeInviteOnboarding(
  input: CompleteInviteOnboardingInput,
): Promise<AuthSession> {
  const data = await apiMutate<unknown>("POST", "/auth/complete-invite-onboarding", {
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
  const parsed = authResponseSchema.parse(data);
  if (parsed.mfaRequired || !parsed.accessToken || !parsed.user) {
    throw new Error("Invalid invitation onboarding response");
  }
  return {
    accessToken: parsed.accessToken,
    companySwitchOptions: parsed.companySwitchOptions,
    user: parsed.user,
  };
}

/** Accepts a pending invitation for the authenticated user's next tenant. */
export async function switchCompany(inviteId: string): Promise<AuthSession> {
  const data = await apiMutate<unknown>("POST", "/auth/switch-company", {
    body: { inviteId },
    idempotencyKey: newIdempotencyKey(),
  });
  const parsed = authResponseSchema.parse(data);
  if (parsed.mfaRequired || !parsed.accessToken || !parsed.user) {
    throw new Error("Invalid company switch response");
  }
  return {
    accessToken: parsed.accessToken,
    companySwitchOptions: parsed.companySwitchOptions,
    user: parsed.user,
  };
}

/** `POST /v1/auth/forgot-password`. Always succeeds regardless of whether the email exists. */
export async function forgotPassword(email: string): Promise<void> {
  await apiMutate<unknown>("POST", "/auth/forgot-password", {
    body: { email },
    idempotencyKey: newIdempotencyKey(),
  });
}

/** `POST /v1/auth/reset-password`. `token` comes from the `?token=` query param. */
export async function resetPassword(input: ResetPasswordInput): Promise<void> {
  await apiMutate<unknown>("POST", "/auth/reset-password", {
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
}

/** `POST /v1/auth/logout`. Requires the current bearer token (attached automatically by the API client). */
export async function logout(): Promise<void> {
  await apiMutate<unknown>("POST", "/auth/logout", {
    body: undefined,
    idempotencyKey: newIdempotencyKey(),
  });
}

/** Ends every session, including the browser session that called this endpoint — the caller must sign in again. */
export async function revokeAllSessions(): Promise<void> {
  await apiMutate<unknown>("POST", "/auth/sessions/revoke-all-and-logout", {
    body: undefined,
    idempotencyKey: newIdempotencyKey(),
  });
}

/** Updates only self-service profile fields (display name, avatar); roles and tenant membership remain server-controlled. Distinct from `updateProfile` (`PATCH /auth/me`, email + tenant name, full replace). */
export async function updateProfileAvatarAndName(
  input: ProfileAvatarInput,
): Promise<User> {
  const data = await apiMutate<unknown>("PATCH", "/auth/me/avatar", {
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
  return userSchema.parse(data);
}

/** The only frontend path for avatar uploads. */
export async function uploadAvatar(file: File): Promise<AvatarUpload> {
  const formData = new FormData();
  formData.set("file", file);
  const data = await apiUpload<unknown>("/uploads/avatar", formData, newIdempotencyKey());
  return avatarUploadSchema.parse(data);
}

/** `GET /v1/auth/me` — source of truth for "am I logged in" and "who am I". */
export async function fetchMe(): Promise<User> {
  const data = await apiGet<unknown>("/auth/me");
  return userSchema.parse(data);
}

/** `PATCH /v1/auth/me` — updates the signed-in user's profile. */
export async function updateProfile(input: ProfileInput): Promise<User> {
  const data = await apiMutate<unknown>("PATCH", "/auth/me", {
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
  return userSchema.parse(data);
}

/** `POST /v1/auth/change-password` — reauthenticates with password or Google. */
export async function changePassword(input: ChangePasswordInput): Promise<void> {
  await apiMutate<unknown>("POST", "/auth/change-password", {
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
}

/** `GET /v1/auth/mfa` — returns the signed-in user's MFA posture. */
export async function getMfaStatus(): Promise<MfaStatus> {
  const data = await apiGet<unknown>("/auth/mfa");
  return mfaStatusSchema.parse(data);
}

export async function getMfaPolicy(): Promise<MfaPolicy> {
  const data = await apiGet<unknown>("/auth/mfa/policy");
  return mfaPolicySchema.parse(data);
}

export async function updateMfaPolicy(policy: {
  enforcementMode: "always" | "conditional";
  trustedDeviceDays: number;
  requireNewDevice: boolean;
  requireNewBrowser: boolean;
  requireNewCountry: boolean;
}): Promise<MfaPolicy> {
  const data = await apiMutate<unknown>("PATCH", "/auth/mfa/policy", {
    body: policy,
    idempotencyKey: newIdempotencyKey(),
  });
  return mfaPolicySchema.parse(data);
}

export async function getMfaTrustedDevices(): Promise<MfaTrustedDevice[]> {
  const data = await apiGet<unknown>("/auth/mfa/trusted-devices");
  return z.array(mfaTrustedDeviceSchema).parse(data);
}

export async function revokeMfaTrustedDevice(deviceId: string): Promise<void> {
  await apiMutate<unknown>("DELETE", `/auth/mfa/trusted-devices/${deviceId}`, {
    body: undefined,
    idempotencyKey: newIdempotencyKey(),
  });
}

/** Starts TOTP enrollment after re-authenticating with password or Google. */
export async function startMfaEnrollment(
  input: MfaEnrollmentInput,
): Promise<{ otpauthUri: string }> {
  const data = await apiMutate<unknown>("POST", "/auth/mfa/totp/enroll", {
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
  return mfaEnrollmentSchema.parse(data);
}

/** Confirms the authenticator code and returns one-time recovery codes. */
export async function confirmMfaEnrollment(
  input: MfaConfirmInput,
): Promise<{ recoveryCodes: string[] }> {
  const data = await apiMutate<unknown>("POST", "/auth/mfa/totp/confirm", {
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
  return mfaRecoveryCodesSchema.parse(data);
}

/** Disables TOTP after password/Google re-authentication and factor verification. */
export async function disableMfa(input: MfaDisableInput): Promise<string> {
  const data = await apiMutate<unknown>("POST", "/auth/mfa/totp/disable", {
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
  return messageResponseSchema.parse(data).message;
}
